import { DefaultRootState } from 'react-redux';

import createReducer, { ActionCreatorHelper } from 'store/createReducer';

export enum AuctionCampaignStatus {
    Blocked = 'BLOCKED',
    Active = 'ACTIVE',
    CampaignLowBalance = 'CAMPAIGN_LOW_BALANCE',
    AccountLowBalance = 'ACCOUNT_LOW_BALANCE',
    ActiveWithDelayedStart = 'ACTIVE_WITH_DELAYED_START',
    InactiveDueToEndDate = 'INACTIVE_DUE_TO_END_DATE',
}

export interface CampaignAccountInfo {
    id: number;
    main: boolean;
    title: string;
}

export interface AuctionCampaign {
    vacancyId: string;
    campaignId: string;
    campaignStatus: AuctionCampaignStatus;
    bid: number;
    budget: number;
    campaignAccountInfo: CampaignAccountInfo;
    campaignBalance: number;
    startAt: string | null;
    endAt: string | null;
    biddingStrategy?: BiddingStrategy;
}

interface AuctionMoneyControl {
    value: number | null;
    minValue: number;
    disabled: boolean;
    initialValue: number;
    initialMinValue?: number;
}

export enum BidFrequencyStatus {
    Rare = 'RARE',
    Average = 'AVERAGE',
    Often = 'OFTEN',
}

export enum AuctionPeriodType {
    Unlimited = 'UNLIMITED',
    Manual = 'MANUAL_PERIOD',
}

export interface BidRating {
    maxBidAmongSimilarVacancies: number | null;
    bidFrequencyStatus: BidFrequencyStatus;
    oftenThresholdBid: number;
    averageThresholdBid?: number;
    competingCampaignBids: number[];
    competingCampaignIds: number[];
    winProbability: number;
    audience: {
        audiencePerWeek: number;
        audienceCapacityPerWeek: number;
        audienceFillRate: number;
    } | null;
}

export interface BidRatingParams {
    name: string;
    text: string;
    vacancyId: number;
    areaIds: number[];
    professionalRoleIds: number[];
}

export enum BiddingStrategy {
    Auto = 'BUDGET_PACING',
    Manual = 'FIXED_BID',
}

export enum AuctionDataField {
    AuctionCampaign = 'auctionCampaign',
    Bid = 'bid',
    BidRating = 'bidRating',
    BidRatingParams = 'bidRatingParams',
    Budget = 'budget',
    StartDate = 'startDate',
    EndDate = 'endDate',
    StartAt = 'startAt',
    EndAt = 'endAt',
    VacancyId = 'vacancyId',
    Vacancy = 'vacancy',
    BiddingStrategy = 'biddingStrategy',
    SelectedPeriodType = 'selectedPeriodType',
    IsDatesChanged = 'isDatesChanged',
}

export enum AuctionBuilderBidErrorCode {
    AutoStrategyRangeUnderflow = 'autoStrategyRangeUnderflow',
    BidCanNotBeChangedForFixedPriceAccount = 'bidCanNotBeChangedForFixedPriceAccount',
    BidCanNotBeMoreThanCampaignBalance = 'bidCanNotBeMoreThanCampaignBalance',
    ValueMissing = 'valueMissing',
    RangeUnderflow = 'rangeUnderflow',
    RangeOverflow = 'rangeOverflow',
}

export enum AuctionBuilderBudgetErrorCode {
    RangeUnderflow = 'rangeUnderflow',
    ValueMissing = 'valueMissing',
    BudgetCanNotBeLessThanAlreadySpent = 'budgetCanNotBeLessThanAlreadySpent',
}

export enum AuctionBuilderDatesErrorCode {
    ValueMissingForStrategy = 'valueMissingForStrategy',
    EndDateBiggerThanStartDate = 'endDateBiggerThanStartDate',
}

export enum CommonErrorCode {
    LowBalance = 'accountLowBalance',
}

export interface AuctionBuilderErrors {
    [AuctionDataField.Bid]?: AuctionBuilderBidErrorCode;
    [AuctionDataField.Budget]?: AuctionBuilderBudgetErrorCode;
    [AuctionDataField.StartAt]?: AuctionBuilderDatesErrorCode;
    [AuctionDataField.EndAt]?: AuctionBuilderDatesErrorCode;
    message?: CommonErrorCode;
}

interface AuctionVacancy {
    vacancyId: number;
    name: string;
}

const removeErrorKey = (errors: AuctionBuilderErrors, fieldName: keyof AuctionBuilderErrors): AuctionBuilderErrors => {
    const nextErrors = { ...errors };
    delete nextErrors[fieldName];
    return nextErrors;
};

// ----------------------- actions -------------------------------------
const SET_AUCTION = 'SET_AUCTION';
const SET_AUCTION_BID_VALUE = 'SET_AUCTION_BID_VALUE';
const SET_AUCTION_BID_MIN_VALUE = 'SET_AUCTION_BID_MIN_VALUE';
const SET_AUCTION_BID_RATING = 'SET_AUCTION_BID_RATING';
const SET_AUCTION_BUDGET_VALUE = 'SET_AUCTION_BUDGET_VALUE';
const SET_AUCTION_START_DATE = 'SET_AUCTION_START_DATE';
const SET_AUCTION_END_DATE = 'SET_AUCTION_END_DATE';
const SET_SELECTED_PERIOD_TYPE = 'SET_SELECTED_PERIOD_TYPE';
const SET_IS_DATES_CHANGED = 'SET_IS_DATES_CHANGED';
const SET_AUCTION_BIDDING_STRATEGY = 'SET_AUCTION_BIDDING_STRATEGY';
const SET_AUCTION_VACANCY_ID = 'SET_AUCTION_VACANCY_ID';
const SET_AUCTION_ERRORS = 'SET_AUCTION_ERRORS';
const CLEAR_AUCTION_ERROR = 'CLEAR_AUCTION_ERROR';
const RESET_DATES = 'RESET_DATES';

interface PayloadTypes {
    [SET_AUCTION]: AuctionData;
    [SET_AUCTION_BID_VALUE]: number | null;
    [SET_AUCTION_BID_MIN_VALUE]: number;
    [SET_AUCTION_BID_RATING]: BidRating;
    [SET_AUCTION_BUDGET_VALUE]: number | null;
    [SET_AUCTION_START_DATE]: string | null;
    [SET_AUCTION_END_DATE]: string | null;
    [SET_SELECTED_PERIOD_TYPE]: AuctionPeriodType;
    [SET_IS_DATES_CHANGED]: boolean;
    [SET_AUCTION_BIDDING_STRATEGY]: BiddingStrategy;
    [SET_AUCTION_VACANCY_ID]: string | null;
    [SET_AUCTION_ERRORS]: AuctionBuilderErrors;
    [CLEAR_AUCTION_ERROR]: keyof AuctionBuilderErrors;
    [RESET_DATES]: null;
}

const ActionCreator = ActionCreatorHelper<PayloadTypes>();

export const setAuction = ActionCreator(SET_AUCTION);
export const setBidValue = ActionCreator(SET_AUCTION_BID_VALUE);
export const setMinBidValue = ActionCreator(SET_AUCTION_BID_MIN_VALUE);
export const setBidRating = ActionCreator(SET_AUCTION_BID_RATING);
export const setBudgetValue = ActionCreator(SET_AUCTION_BUDGET_VALUE);
export const setStartDate = ActionCreator(SET_AUCTION_START_DATE);
export const setEndDate = ActionCreator(SET_AUCTION_END_DATE);
export const setSelectedPeriodType = ActionCreator(SET_SELECTED_PERIOD_TYPE);
export const setIsDatesChanged = ActionCreator(SET_IS_DATES_CHANGED);
export const setBiddingStrategy = ActionCreator(SET_AUCTION_BIDDING_STRATEGY);
export const setVacancyId = ActionCreator(SET_AUCTION_VACANCY_ID);
export const setErrors = ActionCreator(SET_AUCTION_ERRORS);
export const resetDates = ActionCreator(RESET_DATES);
export const clearAuctionErrors = ActionCreator(CLEAR_AUCTION_ERROR);
// ----------------------- /actions -------------------------------------

// ----------------------- selectors ------------------------------------
const createSelector =
    <K extends keyof AuctionData>(key: K) =>
    (state: DefaultRootState) =>
        state.auctionData?.[key] ?? null;

const createSelectorNonNullable =
    <K extends keyof AuctionData>(key: K) =>
    (state: DefaultRootState) =>
        (state.auctionData as AuctionData)[key];

const createErrorSelector =
    <K extends keyof AuctionBuilderErrors>(key: K) =>
    (state: DefaultRootState) =>
        state.auctionData?.errors?.[key] ?? null;

export const auctionSelector = {
    [AuctionDataField.AuctionCampaign]: createSelector(AuctionDataField.AuctionCampaign),
    [AuctionDataField.BidRating]: createSelector(AuctionDataField.BidRating),
    [AuctionDataField.Budget]: createSelector(AuctionDataField.Budget),
    [AuctionDataField.StartDate]: createSelector(AuctionDataField.StartDate),
    [AuctionDataField.EndDate]: createSelector(AuctionDataField.EndDate),
    [AuctionDataField.VacancyId]: createSelector(AuctionDataField.VacancyId),
    [AuctionDataField.Vacancy]: createSelector(AuctionDataField.Vacancy),
    errors: {
        [AuctionDataField.Bid]: createErrorSelector(AuctionDataField.Bid),
        [AuctionDataField.Budget]: createErrorSelector(AuctionDataField.Budget),
        [AuctionDataField.StartAt]: createErrorSelector(AuctionDataField.StartAt),
        [AuctionDataField.EndAt]: createErrorSelector(AuctionDataField.EndAt),
        message: createErrorSelector('message'),
    },
};

export const auctionSelectorNonNullable = {
    [AuctionDataField.Bid]: createSelectorNonNullable(AuctionDataField.Bid),
    [AuctionDataField.BidRatingParams]: createSelectorNonNullable(AuctionDataField.BidRatingParams),
    [AuctionDataField.Budget]: createSelectorNonNullable(AuctionDataField.Budget),
    [AuctionDataField.BiddingStrategy]: createSelectorNonNullable(AuctionDataField.BiddingStrategy),
    [AuctionDataField.SelectedPeriodType]: createSelectorNonNullable(AuctionDataField.SelectedPeriodType),
    [AuctionDataField.IsDatesChanged]: createSelectorNonNullable(AuctionDataField.IsDatesChanged),
};
// ----------------------- /selectors ------------------------------------

// ----------------------- reducer --------------------------------------
export interface AuctionData {
    [AuctionDataField.AuctionCampaign]: AuctionCampaign | null;
    [AuctionDataField.Bid]: AuctionMoneyControl;
    [AuctionDataField.BidRating]: BidRating;
    [AuctionDataField.BidRatingParams]: BidRatingParams;
    [AuctionDataField.Budget]: AuctionMoneyControl;
    [AuctionDataField.StartDate]: string | null;
    [AuctionDataField.EndDate]: string | null;
    [AuctionDataField.VacancyId]: string | null;
    [AuctionDataField.Vacancy]: AuctionVacancy;
    [AuctionDataField.BiddingStrategy]: BiddingStrategy;
    [AuctionDataField.SelectedPeriodType]: AuctionPeriodType;
    [AuctionDataField.IsDatesChanged]: boolean;
    errors: AuctionBuilderErrors;
}

const AUCTION_DATA_INITIAL_STATE: AuctionData | null = null;

const createReducerForField =
    <K extends keyof AuctionData>(key: K) =>
    (state: AuctionData | null, { payload }: { payload: AuctionData[K] }): AuctionData | null =>
        state
            ? {
                  ...state,
                  [key]: payload,
              }
            : null;

const createReducerForInputValue =
    <K extends AuctionDataField.Bid | AuctionDataField.Budget>(key: K) =>
    (state: AuctionData | null, { payload }: { payload: AuctionData[K]['value'] }) =>
        state
            ? {
                  ...state,
                  [key]: { ...state[key], value: payload },
                  errors: removeErrorKey(state.errors, key),
              }
            : null;

const setBidMinValueReducer = (state: AuctionData | null, { payload }: { payload: number }): AuctionData | null =>
    state
        ? {
              ...state,
              [AuctionDataField.Bid]: { ...state[AuctionDataField.Bid], minValue: payload },
          }
        : null;

export default createReducer<AuctionData | null, PayloadTypes>(AUCTION_DATA_INITIAL_STATE, {
    [SET_AUCTION]: (state, { payload }) => payload,
    [SET_AUCTION_BID_RATING]: createReducerForField(AuctionDataField.BidRating),
    [SET_AUCTION_BID_VALUE]: createReducerForInputValue(AuctionDataField.Bid),
    [SET_AUCTION_BID_MIN_VALUE]: setBidMinValueReducer,
    [SET_AUCTION_BUDGET_VALUE]: createReducerForInputValue(AuctionDataField.Budget),
    [SET_AUCTION_START_DATE]: createReducerForField(AuctionDataField.StartDate),
    [SET_AUCTION_END_DATE]: createReducerForField(AuctionDataField.EndDate),
    [SET_SELECTED_PERIOD_TYPE]: createReducerForField(AuctionDataField.SelectedPeriodType),
    [SET_IS_DATES_CHANGED]: createReducerForField(AuctionDataField.IsDatesChanged),
    [SET_AUCTION_BIDDING_STRATEGY]: createReducerForField(AuctionDataField.BiddingStrategy),
    [SET_AUCTION_VACANCY_ID]: createReducerForField(AuctionDataField.VacancyId),
    [SET_AUCTION_ERRORS]: (state, { payload }) =>
        state ? { ...state, errors: { ...state.errors, ...payload } } : null,
    [CLEAR_AUCTION_ERROR]: (state, { payload }) =>
        state ? { ...state, errors: removeErrorKey(state.errors, payload) } : null,
    [RESET_DATES]: (state) =>
        state ? { ...state, [AuctionDataField.StartDate]: null, [AuctionDataField.EndDate]: null } : null,
});
// ----------------------- /reducer --------------------------------------
